// NotifyReport.js - Updated version
import * as moment from "moment";
import { Box, Button, Container, Flex, Text, Modal, TextInput } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useMediaQuery } from "@mantine/hooks";
import { IconDownload, IconPrinter, IconTruck } from "@tabler/icons";
import FloatingMenu from "components/FloatingMenu";
import { PrintModal } from "components/PrintModal";
import { MantineReactTable } from "mantine-react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useQuery } from "react-query";
import { getHeaders, getCsvContent } from "services/filterHelperFunctions";
import { showErrorToast, showSuccessToast } from "utilities/Toast";
import { report_api } from "./report.service";
import AppBreadcrumb from "../../../components/AppBreadcrumb";
import { useReactToPrint } from "react-to-print";


export default function NotifyReport() {
  const [head, setHead] = useState([]);
  const [body, setBody] = useState([]);
  const [foot, setFoot] = useState([]);
  const [csv, setCsv] = useState([]);
  const [isCsv, setIsCsv] = useState(false);
  const [date, setDate] = useState(moment());
  let isMobileScreen = useMediaQuery("(max-width: 768px)");
  const [columnFilters, setColumnFilters] = useState([]);
  const [filename, setFilename] = useState("file");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const componentRef = useRef();
  
  // New state for delivery status feature
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [showDeliveryTable, setShowDeliveryTable] = useState(false);
  const [deliveryData, setDeliveryData] = useState([]);




  // Define columns for Notify Report
  const columns = useMemo(
    () => [
      {
        header: "Product Code",
        accessorKey: "product_code",
        size: "auto",
        filterVariant: "text",
      },
      {
        header: "Product",
        accessorKey: "title",
        size: "auto",
        filterVariant: "text",
      },
      {
        header: "User",
        accessorKey: "username",
        size: "auto",
        filterVariant: "text",
      },
      {
        header: "Status",
        accessorKey: "status",
        size: "auto",
        filterVariant: "text",
      },
      {
        header: "Notify Date",
        accessorKey: "notify_date",
        Cell: ({ row }) => (
          <span>
            {row.original.notify_date
              ? moment(row.original.notify_date).format("DD-MM-YYYY")
              : "-"}
          </span>
        ),
        size: "auto",
        filterVariant: "date-range",
      },
         {
      header: "Stock Qty",
      accessorKey: "stock_qty",
      size: "auto",
      filterVariant: "text",
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return Number(value).toFixed(3);   // always 3 decimals
      }},
      {
        header: "Min Qty",
        accessorKey: "min_qty",
        size: "auto",
        filterVariant: "text",
      },
    ],
    []
  );

  // Define columns for Delivery Status Table
const deliveryColumns = useMemo(
  () => [
    {
      header: "Product",
      accessorKey: "title",
      size: "auto",
    },
    {
      header: "Notify Date",
      accessorKey: "notify_date",
      Cell: ({ row }) => (
        <span>
          {row.original.notify_date
            ? moment(row.original.notify_date).format("DD-MM-YYYY")
            : "-"}
        </span>
      ),
      size: "auto",
    },
    {
      header: "Status",
      accessorKey: "status",
      Cell: ({ row }) => {
        const status = row.original.status;
        const dateTime = row.original.status_updated_at;

        let color = "gray";
        if (status === "Notified") color = "orange";
        else if (status === "Available") color = "green";

        return (
          <span style={{ color, fontWeight: "bold" }}>
            {status}
            {dateTime && (
              <span style={{ color: "#555", fontSize: "11px", marginLeft: "6px" }}>
                ({moment(dateTime).format("DD-MM-YYYY hh:mm A")})
              </span>
            )}
          </span>
        );
      },
      size: "auto",
    },
    {
      header: "Add_Cart Status",
      accessorKey: "add_cart",
      Cell: ({ row }) => {
        const isAdded = row.original.add_cart;
        const dateTime = row.original.add_cart_updated_at;
        return (
          <span style={{ color: isAdded ? "green" : "red", fontWeight: "bold" }}>
            {isAdded ? "Add to Cart" : "Pending"}
            {dateTime && (
              <span style={{ color: "#555", fontSize: "11px", marginLeft: "6px" }}>
                ({moment(dateTime).format("DD-MM-YYYY hh:mm A")})
              </span>
            )}
          </span>
        );
      },
      size: "auto",
    },
    {
      header: "Purchase Status",
      accessorKey: "delivery_status",
      Cell: ({ row }) => {
        const value = row?.original?.delivery_status || "Pending";
        const dateTime = row?.original?.delivery_status_updated_at || null;

        let color = "gray";
        if (value === "accepted") color = "blue";
        else if (value === "packed") color = "orange";
        else if (value === "delivered") color = "green";
        else if (value === "cancelled" || value === "rejected") color = "red";

        const formattedTime =
          dateTime && moment(dateTime).isValid()
            ? moment(dateTime).format("DD-MM-YYYY hh:mm A")
            : null;

        return (
          <div style={{ fontWeight: "bold", color }}>
            {value ? value.charAt(0).toUpperCase() + value.slice(1) : "Pending"}
            {formattedTime && (
              <span
                style={{
                  color: "#555",
                  fontSize: "11px",
                  marginLeft: "6px",
                }}
              >
                ({formattedTime})
              </span>
            )}
          </div>
        );
      },
      size: "auto",
    },
  ],
  [deliveryData]
);


  // Fetch main notify data using react-query
  const notifyData = useQuery({
    queryKey: ["notify-report", pagination.pageIndex, pagination.pageSize, columnFilters],
    queryFn: async () => {
      let filters = {};

      columnFilters.forEach(f => {
        if (f.id === "notify_date" && f.value) {
          if (Array.isArray(f.value)) {
            if (f.value[0]) filters.start_date = moment(f.value[0]).format("YYYY-MM-DD");
            if (f.value[1]) filters.end_date = moment(f.value[1]).format("YYYY-MM-DD");
          } else {
            filters.date = moment(f.value).format("YYYY-MM-DD");
          }
        } else if (f.value) {
          filters[f.id] = f.value;
        }
      });

      const params = new URLSearchParams({
        page: pagination.pageIndex + 1,
        page_size: pagination.pageSize,
        ...filters,
      }).toString();

      return await report_api.get_notify_report(params);
    },
  });

  // Fetch delivery status data
// In the fetchDeliveryStatus function, update the logic:
const fetchDeliveryStatus = async (mobileNo) => {
  try {
    const response = await report_api.get_delivery_status(mobileNo);

    if (response.success) {
      const newData = response.data || [];

      setDeliveryData((prevData) =>
        newData.map((item) => {
          const oldItem = prevData.find((p) => p.id === item.id);

          // ✅ Always prefer backend timestamp if present
          let updatedAt = item.delivery_status_updated_at || oldItem?.delivery_status_updated_at || null;

          // ✅ If status changed locally compared to previous render, update timestamp now
          if (oldItem && oldItem.delivery_status !== item.delivery_status) {
            updatedAt = new Date().toISOString(); // use ISO for consistency
          }

          return {
            ...item,
            delivery_status_updated_at: updatedAt,
          };
        })
      );

      setShowDeliveryTable(true);
      showSuccessToast(`Found ${response.count} records for ${mobileNo}`);
    } else {
      showErrorToast(response.message || "No data found");
      setDeliveryData([]);
      setShowDeliveryTable(false);
    }
  } catch (error) {
    console.log("DEBUG Error:", error);
    showErrorToast({ title: "Error", message: "Error fetching delivery status" });
    setDeliveryData([]);
    setShowDeliveryTable(false);
  }
};


  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  useEffect(() => {
    if (body.length !== 0 && head.length !== 0) {
      if (!isCsv) {
        handlePrint();
        setBody([]);
        setHead([]);
      }
    }
    setIsCsv(false);
  }, [body, head]);

  const printFilter = async () => {
    let rows = [];
    const dataToPrint = showDeliveryTable ? deliveryData : (notifyData.data?.data || []);

    dataToPrint.forEach(e => {
      let cell = [];
      if (showDeliveryTable) {
        cell.push(e?.mobile_no || "-");
        cell.push(e?.title || "-");
        cell.push(e?.status || "-");
        cell.push(e?.notify_date ? moment(e.notify_date).format("DD-MM-YYYY") : "-");
        cell.push(e?.stock_qty || "-");
      } else {
        cell.push(e?.title || "-");
        cell.push(e?.username || "-");
        cell.push(e?.status || "-");
        cell.push(e?.notify_date ? moment(e.notify_date).format("DD-MM-YYYY") : "-");
        cell.push(e?.stock_qty || "-");
        cell.push(e?.min_qty || "-");
      }
      rows.push(cell);
    });

    const currentColumns = showDeliveryTable ? deliveryColumns : columns;
    const head_content = getHeaders(currentColumns);
    const body_content = rows;
    const title = showDeliveryTable ? "DELIVERY_STATUS_REPORT" : "NOTIFY_REPORT";
    const csv_content = getCsvContent(head_content, body_content, [title]);

    setFilename(title + "_" + new Date().toLocaleDateString() + ".csv");
    setCsv(csv_content);
    setHead(head_content);
    setBody(body_content);
  };

  const handleDeliveryStatusSearch = () => {
    if (!mobileNumber.trim()) {
      showErrorToast("Please enter mobile number");
      return;
    }
    fetchDeliveryStatus(mobileNumber.trim());
    setDeliveryModalOpen(false);
  };

  const handleBackToNotify = () => {
    setShowDeliveryTable(false);
    setDeliveryData([]);
    setMobileNumber("");
  };

  // CSV download effect
  const csvRef = useRef();
  useEffect(() => {
    if (csv.length && isCsv) {
      csvRef.current.link.click();
      setCsv([]);
      setIsCsv(false);
    }
  }, [csv]);

  return (
    <>
      <CSVLink
        style={{ display: "none" }}
        asyncOnClick={true}
        data={csv}
        filename={filename}
        ref={csvRef}
      />
      
      {/* Delivery Status Modal */}
      <Modal
        opened={deliveryModalOpen}
        onClose={() => setDeliveryModalOpen(false)}
        title="Check Delivery Status"
        size="sm"
      >
        <Box>
          <TextInput
            label="Enter Mobile Number"
            placeholder="Enter mobile number"
            value={mobileNumber}
            onChange={(e) => setMobileNumber(e.target.value)}
            mb="md"
          />
          <Button
            fullWidth
            onClick={handleDeliveryStatusSearch}
            leftIcon={<IconTruck size={16} />}
          >
            Search Delivery Status
          </Button>
        </Box>
      </Modal>

      <Container fluid>
        <Box p={25}>
          <Flex justify={"space-between"} align="end">
            <Box>
              <Text size={"lg"} fw={600} mb={10}>
                {showDeliveryTable ? "Delivery Status" : "Notify Report"}
              </Text>
              <AppBreadcrumb />
            </Box>
            <Flex gap={10} align={"flex-end"}>
              {showDeliveryTable && (
                <Button
                  variant="outline"
                  onClick={handleBackToNotify}
                >
                  Back to Notify Report
                </Button>
              )}
              
              <Button
                leftIcon={<IconTruck />}
                onClick={() => setDeliveryModalOpen(true)}
                variant={showDeliveryTable ? "filled" : "outline"}
              >
                Delivery Status
              </Button>
              
              <Button
                onClick={() => {
                  setIsCsv(true);
                  printFilter();
                }}
                leftIcon={<IconDownload />}
              >
                CSV
              </Button>
              
              <Button
                leftIcon={<IconPrinter />}
                onClick={printFilter}
              >
                Print
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Container>

      <Container fluid mt={10} mb={30}>
        <Box px={isMobileScreen ? 5 : 25} sx={{ borderRadius: 2 }}>
          <Box className="datatable-wrapper">
            {showDeliveryTable ? (
              <MantineReactTable
                columns={deliveryColumns}
                data={deliveryData}
                mantineTableProps={{ className: "datatable" }}
                renderTopToolbarCustomActions={() => (
                  <Box px={5} py={10}>
                    <Text
                      fw={600}
                      tt="uppercase"
                      size="sm"
                      sx={theme => ({
                        letterSpacing: 1,
                        color: theme.colors.brand[7],
                      })}
                    >
                      Delivery Status for: {mobileNumber}
                    </Text>
                  </Box>
                )}
                initialState={{ density: "xs" }}
                enableStickyHeader
                enableGlobalFilter={false}
                enableColumnFilters={false}
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                enableColumnActions={false}
              />
            ) : (
              <MantineReactTable
                manualFiltering
                manualPagination
                onColumnFiltersChange={setColumnFilters}
                onPaginationChange={setPagination}
                state={{ columnFilters, pagination }}
                mantineTableProps={{ className: "datatable" }}
                renderTopToolbarCustomActions={() => (
                  <Box px={5} py={10}>
                    <Text
                      fw={600}
                      tt="uppercase"
                      size="sm"
                      sx={theme => ({
                        letterSpacing: 1,
                        color: theme.colors.brand[7],
                      })}
                    >
                      Notify List
                    </Text>
                  </Box>
                )}
                initialState={{ density: "xs" }}
                columns={columns}
                data={notifyData.data?.data || []}
                rowCount={notifyData.data?.count || 0}
                enableStickyHeader
                enableGlobalFilter={false}
                enableColumnFilters
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                enableColumnActions={false}
              />
            )}
          </Box>
        </Box>
      </Container>
      
      <div style={{ display: "none" }}>
        <PrintModal
          title={showDeliveryTable ? "Delivery Status Report" : "Notify Report"}
          head={head}
          body={body}
          foot={foot}
          ref={componentRef}
        />
      </div>

      {/* Mobile floating menus */}
      {isMobileScreen && (
        <>
          <FloatingMenu
            m={5}
            ml={60}
            left
            size={50}
            onClick={() => {
              setIsCsv(true);
              printFilter();
            }}
          >
            <IconDownload color="white" />
          </FloatingMenu>
          <FloatingMenu
            m={5}
            right
            size={50}
            onClick={() => setDeliveryModalOpen(true)}
          >
            <IconTruck color="white" />
          </FloatingMenu>
          <FloatingMenu
            m={5}
            right
            size={50}
            style={{ bottom: "70px" }}
            onClick={printFilter}
          >
            <IconPrinter color="white" />
          </FloatingMenu>
        </>
      )}
    </>
  );
}